/**
 * @fileoverview Estilos da página de Faturamento dos planos novos (etapa 7): os
 * cartões das Configurações (borda fina, raio 12), o plano numa frase com a
 * mensalidade em número grande e tabular, o uso em linhas com barras finas
 * neutras (cor só no estado das mensagens, que acabam: âmbar a partir de 80%,
 * vermelho no limite) e as faturas numa tabela simples. O espaço entre os
 * cartões é o da página das Configurações.
 */

import { makeStyles } from "@material-ui/core/styles";

import { tokens } from "../Triage/styles";

const useBillingOverviewStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const c = tokens(dark);
  const text = dark ? "#f8fafc" : "#111827";
  const focus = `0 0 0 1px ${dark ? "rgba(255,255,255,0.7)" : "rgba(15,23,42,0.7)"}`;
  const xs = theme.breakpoints.down("xs");
  const reduce = "@media (prefers-reduced-motion: reduce)";

  return {
    card: { padding: theme.spacing(2.5), borderRadius: 12, background: c.surface, border: `1px solid ${c.border}` },
    cardHead: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" },
    cardTitle: { margin: 0, fontSize: "1rem", fontWeight: 700, color: text, display: "flex", alignItems: "center", gap: 4 },
    cardMeta: { fontSize: "0.8rem", color: c.textSecondary, fontVariantNumeric: "tabular-nums" },

    // O plano: o nome, a situação, a frase do que está contratado e a mensalidade.
    plan: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, [xs]: { flexDirection: "column", gap: 12 } },
    planMain: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    planNameRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    planName: { margin: 0, fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.01em", color: text },
    pill: { fontSize: "0.72rem", fontWeight: 600, borderRadius: 999, padding: "2px 9px", border: `1px solid ${c.border}`, color: c.textSecondary },
    pillOk: { color: c.ok, borderColor: dark ? "rgba(0,168,132,0.35)" : "rgba(0,113,92,0.3)" },
    pillWait: { color: c.wait, borderColor: dark ? "rgba(251,191,36,0.35)" : "rgba(163,72,11,0.3)" },
    pillBad: { color: c.bad, borderColor: dark ? "rgba(241,92,109,0.35)" : "rgba(198,40,40,0.3)" },
    planSentence: { margin: 0, fontSize: "0.9rem", lineHeight: 1.55, color: c.textSecondary, maxWidth: 560 },
    planRenew: { margin: 0, fontSize: "0.82rem", color: c.textSecondary, fontVariantNumeric: "tabular-nums" },
    price: { textAlign: "right", flexShrink: 0, [xs]: { textAlign: "left" } },
    priceValue: { display: "block", fontSize: "2rem", lineHeight: 1.1, fontWeight: 700, letterSpacing: "-0.02em", color: text, fontVariantNumeric: "tabular-nums" },
    priceUnit: { fontSize: "0.8rem", color: c.textSecondary },
    planActions: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 18 },
    planNote: { margin: "14px 0 0", fontSize: "0.82rem", color: c.textSecondary },
    priceList: { display: "block", marginTop: 2, fontSize: "0.78rem", color: c.textMuted },
    conditionList: { margin: "12px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 2, fontSize: "0.82rem", lineHeight: 1.5, color: c.textSecondary },

    primaryBtn: {
      borderRadius: 8,
      border: "none",
      height: 40,
      padding: "0 18px",
      cursor: "pointer",
      fontSize: "0.88rem",
      fontWeight: 700,
      fontFamily: "inherit",
      color: "#fff",
      background: "#3D5CFD",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      "&:disabled": { opacity: 0.55, cursor: "default" },
      "&:focus-visible": { outline: "none", boxShadow: `0 0 0 1px ${dark ? "#fff" : "#0f172a"}` },
    },
    secondaryBtn: {
      borderRadius: 8,
      border: `1px solid ${c.inputBorder}`,
      height: 40,
      padding: "0 16px",
      cursor: "pointer",
      fontSize: "0.86rem",
      fontWeight: 600,
      fontFamily: "inherit",
      color: text,
      background: "transparent",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      textDecoration: "none",
      // O link com cara de botão (abrir o boleto): o public/index.html pinta todo <a> de verde com
      // !important (os links das mensagens); aqui vale a cor do texto, também visitado e no hover.
      "&:link, &:visited, &[href]:hover": { color: `${text} !important` },
      "& svg": { width: 15, height: 15 },
      "&:hover:not(:disabled)": { background: c.hover },
      "&:disabled": { opacity: 0.55, cursor: "default" },
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },
    dangerBtn: { color: c.bad, borderColor: dark ? "rgba(241,92,109,0.4)" : "rgba(198,40,40,0.35)" },

    // O pedido aguardando pagamento (o boleto).
    pendingText: { margin: "0 0 4px", fontSize: "0.9rem", lineHeight: 1.55, color: c.textSecondary },
    boletoLine: { display: "flex", alignItems: "center", gap: 14, margin: "12px 0 14px", flexWrap: "wrap" },
    boletoLabel: { fontSize: "0.75rem", color: c.textMuted, display: "block", marginBottom: 2 },
    boletoDigits: { flex: "1 1 320px", minWidth: 0, fontSize: "0.95rem", color: text, fontVariantNumeric: "tabular-nums", letterSpacing: "0.01em", wordBreak: "break-word" },
    pendingActions: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
    // O Pix da alteração aguardando pagamento: o mesmo bloco do modal (QR, código e copiar).
    pixBlock: { margin: "4px 0 16px" },
    cancelHint: { fontSize: "0.78rem", color: c.textMuted },

    // Etapa 9: o acesso restrito (no topo), a escolha do que fica, o desfazer do cancelamento e os arquivados.
    restrictedCard: { borderColor: c.warnBorder },
    // A suspensão pelo atraso (etapa 10): a borda do estado, como a do acesso restrito.
    suspendedCard: { borderColor: c.dangerBorder },
    overdueList: { listStyle: "none", margin: "8px 0 12px", padding: 0, display: "flex", flexDirection: "column", gap: 6, maxWidth: 420 },
    overdueItem: { display: "flex", justifyContent: "space-between", gap: 12, fontSize: "0.86rem", color: c.textSecondary },
    refundNote: { display: "block", marginTop: 2, fontSize: "0.8rem", lineHeight: 1.45, color: c.textMuted, overflowWrap: "anywhere" },
    overdueAmount: { color: text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
    choiceRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", margin: "10px 0 14px" },
    choiceWarn: { margin: 0, fontSize: "0.85rem", lineHeight: 1.5, color: c.warn, maxWidth: 560 },
    undoHint: { flexBasis: "100%", margin: "0 0 2px", fontSize: "0.82rem", lineHeight: 1.5, color: c.textSecondary, maxWidth: 600 },
    archivedList: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" },
    archivedRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "10px 0",
      borderTop: `1px solid ${c.border}`,
      "&:first-child": { borderTop: "none", paddingTop: 0 },
      [xs]: { flexDirection: "column", alignItems: "flex-start" },
    },
    archivedMain: { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
    archivedName: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: "0.9rem", fontWeight: 600, color: text },
    archivedType: { fontSize: "0.72rem", fontWeight: 600, borderRadius: 6, padding: "1px 7px", border: `1px solid ${c.border}`, color: c.textSecondary },
    archivedMeta: { fontSize: "0.8rem", color: c.textSecondary, fontVariantNumeric: "tabular-nums" },

    // O uso do mês: uma linha por recurso.
    usageList: { listStyle: "none", margin: 0, padding: 0 },
    usageRow: {
      display: "grid",
      gridTemplateColumns: "180px minmax(0, 1fr) 150px",
      alignItems: "center",
      gap: 16,
      padding: "11px 0",
      borderTop: `1px solid ${c.border}`,
      "&:first-child": { borderTop: "none", paddingTop: 2 },
      [xs]: { gridTemplateColumns: "1fr auto", gap: 8, "& > :nth-child(2)": { gridColumn: "1 / -1", order: 3 } },
    },
    usageName: { fontSize: "0.88rem", color: text },
    usageValue: { fontSize: "0.85rem", color: c.textSecondary, textAlign: "right", fontVariantNumeric: "tabular-nums" },
    meter: { height: 4, borderRadius: 999, background: c.track, overflow: "hidden" },
    meterFill: {
      height: "100%",
      borderRadius: 999,
      background: c.fill,
      transformOrigin: "left center",
      animation: "$grow 420ms cubic-bezier(0.2, 0.8, 0.2, 1)",
      [reduce]: { animation: "none" },
    },
    meterWarn: { background: c.wait },
    meterFull: { background: c.bad },
    meterNone: { background: "transparent" },
    "@keyframes grow": { from: { transform: "scaleX(0)" }, to: { transform: "scaleX(1)" } },

    // As faturas.
    // No celular, cada fatura vira um bloco com os rótulos ao lado (sem rolar para o lado).
    tableWrap: { overflowX: "auto", [xs]: { overflowX: "visible", "& thead": { display: "none" } } },
    tr: { [xs]: { display: "block", padding: "8px 0", borderTop: `1px solid ${c.border}`, "&:first-child": { borderTop: "none" } } },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "0.86rem", fontVariantNumeric: "tabular-nums" },
    th: { textAlign: "left", fontWeight: 600, fontSize: "0.78rem", color: c.textMuted, padding: "0 12px 8px 0", whiteSpace: "nowrap" },
    td: {
      padding: "10px 12px 10px 0",
      borderTop: `1px solid ${c.border}`,
      color: text,
      whiteSpace: "nowrap",
      [xs]: {
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        border: "none",
        padding: "3px 0",
        "&[data-label]::before": { content: "attr(data-label)", color: c.textMuted, fontWeight: 400 },
        "&:empty": { display: "none" },
      },
    },
    tdMuted: { color: c.textSecondary },
    statusOk: { color: c.ok },
    statusWait: { color: c.wait },
    statusBad: { color: c.bad },
    link: {
      "&:link, &:visited, &[href]:hover": { color: `${text} !important` },
      fontWeight: 600,
      textDecoration: "underline",
      textUnderlineOffset: 3,
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },
    // O "Pedir estorno" na linha da fatura: um link, sem cor (etapa 10).
    linkBtn: {
      border: "none",
      background: "transparent",
      padding: 0,
      fontFamily: "inherit",
      fontSize: "inherit",
      fontWeight: 600,
      color: text,
      textDecoration: "underline",
      textUnderlineOffset: 3,
      cursor: "pointer",
      "&:focus-visible": { outline: "none", boxShadow: focus },
    },
    empty: { margin: 0, fontSize: "0.88rem", color: c.textSecondary },
  };
});

export default useBillingOverviewStyles;
